import type { PromptCreative } from "src/pages/ad-studio/insights/ad-action-prompt";
import type {
  AdCreative,
  CreativeChoice,
  CreativeSize,
  GroupSelection,
  LoadedSize,
  MediaItem,
  NonEmptyArray,
  PickedCreative,
  SelectedCreative,
  ShownMedia,
  SizedVideo,
  VideoSource,
} from "src/pages/ad-studio/insights/performance/types";
import type { EntityCardMedia } from "src/pages/ad-studio/insights/performance/view/types";

export const creativeChoiceOf = ({
  shownCreatives,
  adId,
}: {
  shownCreatives: GroupSelection["shownCreatives"];
  adId: string;
}): CreativeChoice => shownCreatives.get(adId) ?? { kind: "first-creative" };

/** A picked creative the ad no longer has falls back to its first. */
export function shownCreativeOf({
  creatives,
  choice,
}: {
  creatives: NonEmptyArray<AdCreative>;
  choice: CreativeChoice;
}): AdCreative {
  switch (choice.kind) {
    case "first-creative": {
      return creatives[0];
    }
    case "picked": {
      return (
        creatives.find(
          (creative) => creative.creativeId === choice.creativeId,
        ) ?? creatives[0]
      );
    }
    default: {
      return choice satisfies never;
    }
  }
}

/** Steps wrap at both ends. */
export function steppedChoiceOf({
  creatives,
  choice,
  step,
}: {
  creatives: NonEmptyArray<AdCreative>;
  choice: CreativeChoice;
  step: 1 | -1;
}): PickedCreative {
  const count = creatives.length;
  const index = creatives.indexOf(shownCreativeOf({ creatives, choice }));
  const next = creatives[(((index + step) % count) + count) % count];
  if (next === undefined) {
    throw new Error("A wrapped index is within the ad's creatives");
  }
  return { kind: "picked", creativeId: next.creativeId };
}

/** The media sizes elements have reported so far, by creative id. */
export type MeasuredSizes = ReadonlyMap<string, LoadedSize>;

const shownVideoOf = ({
  video,
  size,
}: {
  video: VideoSource;
  size: CreativeSize;
}): SizedVideo<CreativeSize> => {
  switch (video.kind) {
    case "file": {
      return { ...video, size };
    }
    case "youtube": {
      return video;
    }
    default: {
      return video satisfies never;
    }
  }
};

const shownMediaOf = ({
  item,
  size,
}: {
  item: MediaItem;
  size: CreativeSize;
}): ShownMedia => {
  switch (item.kind) {
    case "image": {
      return { ...item, size };
    }
    case "video": {
      return {
        kind: "video",
        video: shownVideoOf({ video: item.video, size }),
      };
    }
    default: {
      return item satisfies never;
    }
  }
};

export function selectedCreativeOf({
  position,
  sizes,
}: {
  position: EntityCardMedia;
  sizes: MeasuredSizes;
}): SelectedCreative {
  switch (position.kind) {
    case "no-media": {
      return { kind: "no-media" };
    }
    case "single":
    case "carousel": {
      return {
        kind: "creative",
        creativeId: position.shown.creativeId,
        media: shownMediaOf({
          item: position.shown.item,
          size: sizes.get(position.shown.creativeId) ?? { kind: "measuring" },
        }),
      };
    }
    default: {
      return position satisfies never;
    }
  }
}

/** The creative as a suggested action hands it to the prompt, once its file has reported a size. */
type PromptCreativeResult =
  | { kind: "measuring" }
  | { kind: "ready"; creative: PromptCreative };

const readyWhenSized = ({
  size,
  media,
}: {
  size: CreativeSize;
  media: (size: LoadedSize) => PromptCreative["media"];
}): PromptCreativeResult => {
  switch (size.kind) {
    case "measuring": {
      return { kind: "measuring" };
    }
    case "measured":
    case "load-failed": {
      return { kind: "ready", creative: { media: media(size) } };
    }
    default: {
      return size satisfies never;
    }
  }
};

const promptMediaOf = (media: ShownMedia): PromptCreativeResult => {
  switch (media.kind) {
    case "image": {
      return readyWhenSized({
        size: media.size,
        media: (size) => ({ ...media, size }),
      });
    }
    case "video": {
      const { video } = media;
      switch (video.kind) {
        case "youtube": {
          return {
            kind: "ready",
            creative: { media: { kind: "video", video } },
          };
        }
        case "file": {
          return readyWhenSized({
            size: video.size,
            media: (size) => ({ kind: "video", video: { ...video, size } }),
          });
        }
        default: {
          return video satisfies never;
        }
      }
    }
    default: {
      return media satisfies never;
    }
  }
};

export function promptCreativeOf(
  creative: SelectedCreative,
): PromptCreativeResult {
  switch (creative.kind) {
    case "no-media": {
      return { kind: "ready", creative: { media: creative } };
    }
    case "creative": {
      return promptMediaOf(creative.media);
    }
    default: {
      return creative satisfies never;
    }
  }
}
